import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Vinci National Price Pulse — monumental brand-by-brand price grid.
 * Each brand renders as an obsidian card with gold serif name, a Live
 * Pulse LED, and its SKU list (name + rate) beneath.
 */
export default function PricePulseGrid() {
  const [state, setState] = useState({ loading: true, brands: [], publishedAt: null });
  const [filter, setFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/price-pulse/live`);
        if (!cancelled) setState({ loading: false, brands: r.data.brands || [], publishedAt: r.data.published_at });
      } catch {
        if (!cancelled) setState({ loading: false, brands: [], publishedAt: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Collect every state seen in the pulse for the map-pulse filter dropdown.
  const states = ["all", ...Array.from(new Set(
    state.brands.flatMap(b => b.items.map(i => i.state).filter(Boolean))
  )).sort()];

  const brands = state.brands
    .map(b => ({
      ...b,
      items: b.items.filter(i =>
        (filter === "all" || i.kind === filter) &&
        (stateFilter === "all" || i.state === stateFilter)
      ),
    }))
    .filter(b => b.items.length > 0);

  return (
    <section className="mt-24 px-6 max-w-[1400px] mx-auto" data-testid="price-pulse-grid">
      <div className="border-l-2 border-[#B87333] pl-6 mb-10">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#B87333]" data-testid="price-pulse-eyebrow">Vinci · National Price Pulse</div>
        <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-[#F5F5F0]" data-testid="price-pulse-title">Live market rates, every morning.</h2>
        <p className="mt-4 font-body text-sm text-[#8A8A82] max-w-2xl">
          A daily-refreshed grid of every cement &amp; TMT steel brand our trade desk tracks nationally.
          Rates land at 06:00 IST and go live once the executive desk signs them off.
        </p>
        {state.publishedAt && (
          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[#5A5A55]" data-testid="price-pulse-published">
            Last published: {new Date(state.publishedAt).toLocaleString()}
          </div>
        )}
      </div>

      <div className="mb-6 flex gap-2 flex-wrap items-center">
        {[["all","All"],["cement","Cement"],["tmt","TMT Steel"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)}
            className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] rounded-sm border transition-colors ${filter===k?"bg-[#D8B23E] text-[#0A0A0A] border-[#D8B23E]":"border-white/15 text-[#C9C9C2] hover:border-[#D8B23E]/60"}`}
            data-testid={`price-pulse-filter-${k}`}>{label}</button>
        ))}
        {states.length > 2 && (
          <>
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#5A5A55] ml-4">National Map Pulse</span>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
              className="px-3 py-1.5 bg-transparent border border-white/15 text-[#C9C9C2] font-body text-[11px] rounded-sm hover:border-[#D8B23E]/60 focus:outline-none focus:border-[#D8B23E]"
              data-testid="price-pulse-state-filter">
              {states.map(s => (
                <option key={s} value={s} className="bg-[#0B0B0B]">
                  {s === "all" ? "All states" : s}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {state.loading && (
        <div className="font-body text-xs text-[#8A8A82]" data-testid="price-pulse-loading">Loading the daily pulse…</div>
      )}
      {!state.loading && brands.length === 0 && (
        <div className="border border-white/10 p-8 rounded-sm font-body text-sm text-[#8A8A82]" data-testid="price-pulse-empty">
          The trade desk hasn't published today's pulse yet — check back after 06:00 IST.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {brands.map((b, idx) => (
          <article key={b.brand} data-testid={`price-pulse-card-${idx}`}
            className="relative bg-gradient-to-b from-[#0B0B0B] to-[#050505] border border-white/10 rounded-sm p-5 hover:border-[#D8B23E]/40 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                {/* Clearbit logo (secondary render). Falls back silently
                    to the gold serif brand name when the domain guess
                    doesn't resolve — no broken image icon shown. */}
                <img
                  src={`https://logo.clearbit.com/${b.brand.toLowerCase().replace(/[^a-z0-9]/g,"")}.com`}
                  alt=""
                  loading="lazy"
                  onError={(e)=>{e.currentTarget.style.display="none";}}
                  className="h-6 w-6 rounded-sm bg-white/5 object-contain shrink-0"
                  data-testid={`price-pulse-logo-${idx}`}
                />
                <h3 className="font-serif text-xl text-[#D8B23E] leading-tight truncate" data-testid={`price-pulse-card-brand-${idx}`}>{b.brand}</h3>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.24em] text-[#2FB999] shrink-0">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2FB999] animate-pulse" />
                Live Pulse
              </span>
            </div>
            <div className="mt-4 space-y-2 border-t border-white/5 pt-3">
              {b.items.slice(0, 6).map((it, j) => (
                <div key={j} className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-body text-[12px] text-[#F5F5F0] truncate">{it.name}</div>
                    <div className="font-mono text-[10px] text-[#5A5A55] uppercase">{it.product_type}</div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="font-mono text-sm text-[#F5F5F0]">₹{Math.round(it.special_price_inr || it.price_inr).toLocaleString()}</div>
                    {it.special_price_inr && (
                      <div className="font-mono text-[9px] text-[#8A8A82] line-through">₹{Math.round(it.price_inr).toLocaleString()}</div>
                    )}
                  </div>
                </div>
              ))}
              {b.items.length > 6 && (
                <div className="pt-1 font-mono text-[10px] text-[#8A8A82]">+ {b.items.length - 6} more SKUs</div>
              )}
            </div>
            <div className="mt-3 font-mono text-[9px] text-[#5A5A55] uppercase tracking-[0.2em]">
              {b.items[0]?.unit || ""} · {b.items.length} SKU{b.items.length===1?"":"s"}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
